import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSAPDataStore } from '../store/SAPDataStore';
import { cx } from '../styles';
import { useThemeClasses } from '../providers/ThemeProvider';

interface TargetManagementProps {
  className?: string;
}

const TargetManagement: React.FC<TargetManagementProps> = ({ className = '' }) => {
  const { t } = useTranslation();
  const themeClasses = useThemeClasses();
  const {
    targetConfiguration,
    targetLoading,
    targetError,
    fetchTargetConfiguration,
    updateMachineTypeTarget,
    getAllMachineTypes,
    getMachineTypeTarget
  } = useSAPDataStore();

  const [selectedMachineType, setSelectedMachineType] = useState<string>('');
  const [efficiencyTarget, setEfficiencyTarget] = useState<string>('');
  const [utilizationTarget, setUtilizationTarget] = useState<string>('');
  const [rejectRatioTarget, setRejectRatioTarget] = useState<string>('');
  const [maxDowntimeTarget, setMaxDowntimeTarget] = useState<string>('');
  const [minMTBFTarget, setMinMTBFTarget] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Get all available machine types
  const machineTypes = getAllMachineTypes();

  // Load target configuration on mount
  useEffect(() => {
    fetchTargetConfiguration();
  }, [fetchTargetConfiguration]);

  // Update form when machine type selection changes
  useEffect(() => {
    if (selectedMachineType) {
      const target = getMachineTypeTarget(selectedMachineType);
      setEfficiencyTarget(target?.targets.efficiency?.toString() || '');
      setUtilizationTarget(target?.targets.utilization?.toString() || '');
      setRejectRatioTarget(target?.targets.rejectRatio?.toString() || '');
      setMaxDowntimeTarget(target?.targets.maxDowntime?.toString() || '');
      setMinMTBFTarget(target?.targets.minMTBF?.toString() || '');
    } else {
      setEfficiencyTarget('');
      setUtilizationTarget('');
      setRejectRatioTarget('');
      setMaxDowntimeTarget('');
      setMinMTBFTarget('');
    }
  }, [selectedMachineType, getMachineTypeTarget]);

  const handleSave = async () => {
    if (!selectedMachineType) {
      alert('Please select a machine type');
      return;
    }

    setSaving(true);
    setSaveSuccess(false);

    try {
      const targets: { 
        efficiency?: number; 
        utilization?: number;
        rejectRatio?: number;
        maxDowntime?: number;
        minMTBF?: number;
      } = {};
      
      if (efficiencyTarget) {
        const efficiency = parseFloat(efficiencyTarget);
        if (isNaN(efficiency) || efficiency < 0 || efficiency > 100) {
          alert('Efficiency target must be a number between 0 and 100');
          setSaving(false);
          return;
        }
        targets.efficiency = efficiency;
      }

      if (utilizationTarget) {
        const utilization = parseFloat(utilizationTarget);
        if (isNaN(utilization) || utilization < 0 || utilization > 100) {
          alert('Utilization target must be a number between 0 and 100');
          setSaving(false);
          return;
        }
        targets.utilization = utilization;
      }

      if (rejectRatioTarget) {
        const rejectRatio = parseFloat(rejectRatioTarget);
        if (isNaN(rejectRatio) || rejectRatio < 0 || rejectRatio > 100) {
          alert('Reject ratio target must be a number between 0 and 100');
          setSaving(false);
          return;
        }
        targets.rejectRatio = rejectRatio;
      }

      if (maxDowntimeTarget) {
        const maxDowntime = parseFloat(maxDowntimeTarget);
        if (isNaN(maxDowntime) || maxDowntime < 0) {
          alert('Max downtime target must be a non-negative number');
          setSaving(false);
          return;
        }
        targets.maxDowntime = maxDowntime;
      }

      if (minMTBFTarget) {
        const minMTBF = parseFloat(minMTBFTarget);
        if (isNaN(minMTBF) || minMTBF <= 0) {
          alert('Min MTBF target must be a positive number');
          setSaving(false);
          return;
        }
        targets.minMTBF = minMTBF;
      }

      await updateMachineTypeTarget(selectedMachineType, targets);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Failed to save target:', error);
      // Don't show an alert for localStorage fallback - it's expected behavior
      if (error instanceof Error && error.message.includes('falling back to localStorage')) {
        // This is actually a success - the target was saved locally
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        alert('Failed to save target configuration');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedMachineType) return;
    
    if (!confirm(`Are you sure you want to delete targets for ${selectedMachineType}?`)) {
      return;
    }

    setSaving(true);
    try {
      await updateMachineTypeTarget(selectedMachineType, {});
      setEfficiencyTarget('');
      setUtilizationTarget('');
      setRejectRatioTarget('');
      setMaxDowntimeTarget('');
      setMinMTBFTarget('');
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Failed to delete target:', error);
      // Don't show an alert for localStorage fallback - it's expected behavior
      if (error instanceof Error && error.message.includes('falling back to localStorage')) {
        // This is actually a success - the target was deleted locally
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        alert('Failed to delete target configuration');
      }
    } finally {
      setSaving(false);
    }
  };

  if (targetLoading && Object.keys(targetConfiguration.targets).length === 0) {
    return (
      <div className={cx('rounded-lg shadow-md p-6', themeClasses.bgSurface, className)}>
        <div className="flex items-center justify-center py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className={themeClasses.textSecondary}>Loading target configuration...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cx('rounded-lg shadow-md p-6', themeClasses.bgSurface, themeClasses.borderPrimary, 'border', className)}>
      <div className="mb-6">
        <h2 className={cx('text-2xl font-bold mb-2', themeClasses.textPrimary)}>
          {t('targetManagement') || 'Target Management'}
        </h2>
        <p className={themeClasses.textSecondary}>
          {t('targetManagementDescription') || 'Configure performance targets for different machine types. These targets will be displayed as reference lines in charts.'}
        </p>
      </div>

      {targetError && (
        <div className={cx(
          'mb-4 p-4 border rounded-md',
          targetError.includes('locally') || targetError.includes('local storage') 
            ? themeClasses.bgWarningSubtle + ' ' + themeClasses.borderSecondary
            : themeClasses.bgErrorSubtle + ' ' + themeClasses.borderSecondary
        )}>
          <div className={cx(
            'font-medium',
            targetError.includes('locally') || targetError.includes('local storage')
              ? themeClasses.textWarning
              : themeClasses.textError
          )}>
            {targetError.includes('locally') || targetError.includes('local storage') 
              ? 'Info' 
              : 'Error'
            }
          </div>
          <div className={cx(
            'text-sm',
            targetError.includes('locally') || targetError.includes('local storage')
              ? 'text-yellow-700 dark:text-yellow-300'
              : 'text-red-700 dark:text-red-300'
          )}>
            {targetError}
          </div>
          {(targetError.includes('locally') || targetError.includes('local storage')) && (
            <div className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
              Targets will still work in charts, but won't be synchronized across devices until backend support is added.
            </div>
          )}
        </div>
      )}

      {saveSuccess && (
        <div className={cx("mb-4 p-4 border rounded-md", themeClasses.bgSuccessSubtle, themeClasses.borderSecondary)}>
          <div className={cx("font-medium", themeClasses.textSuccess)}>✅ Success</div>
          <div className={cx("text-sm", themeClasses.textSuccess)}>
            Target configuration saved to JetStream and will be applied to charts immediately.
            Data is encrypted and synchronized across your devices.
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Panel - Target Configuration Form */}
        <div className="space-y-6">
          <h3 className={cx('text-lg font-semibold', themeClasses.textPrimary)}>Configure Targets</h3>
          
          {/* Machine Type Selection */}
          <div>
            <label className={cx('block text-sm font-medium mb-2', themeClasses.textPrimary)}>
              {t('machineType') || 'Machine Type'}
            </label>
            <select
              value={selectedMachineType}
              onChange={(e) => setSelectedMachineType(e.target.value)}
              className={cx(
                'w-full p-3 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                themeClasses.bgPrimary,
                themeClasses.textPrimary,
                themeClasses.borderPrimary,
                'border'
              )}
            >
              <option value="">Select a machine type...</option>
              {machineTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          {selectedMachineType && (
            <div className="space-y-6">
              {/* Efficiency Target */}
              <div>
                <label className={cx('block text-sm font-medium mb-2', themeClasses.textPrimary)}>
                  {t('efficiencyTarget') || 'Efficiency Target'} (%)
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={efficiencyTarget}
                  onChange={(e) => setEfficiencyTarget(e.target.value)}
                  placeholder="e.g., 70"
                  className={cx(
                    'w-full p-3 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                    themeClasses.bgPrimary,
                    themeClasses.textPrimary,
                    themeClasses.borderPrimary,
                    'border'
                  )}
                />
                <p className={cx('text-sm mt-1', themeClasses.textMuted)}>
                  Target efficiency percentage (0-100). Leave empty to remove target line.
                </p>
              </div>

              {/* Utilization Target */}
              <div>
                <label className={cx('block text-sm font-medium mb-2', themeClasses.textPrimary)}>
                  {t('utilizationTarget') || 'Utilization Target'} (%)
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={utilizationTarget}
                  onChange={(e) => setUtilizationTarget(e.target.value)}
                  placeholder="e.g., 85"
                  className={cx(
                    'w-full p-3 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                    themeClasses.bgPrimary,
                    themeClasses.textPrimary,
                    themeClasses.borderPrimary,
                    'border'
                  )}
                />
                <p className={cx('text-sm mt-1', themeClasses.textMuted)}>
                  Target utilization percentage (0-100). Leave empty to remove target line.
                </p>
              </div>

              {/* Reject Ratio Target */}
              <div>
                <label className={cx('block text-sm font-medium mb-2', themeClasses.textPrimary)}>
                  {t('rejectRatioTarget') || 'Reject Ratio Target'} (%)
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={rejectRatioTarget}
                  onChange={(e) => setRejectRatioTarget(e.target.value)}
                  placeholder="e.g., 0.80"
                  className={cx(
                    'w-full p-3 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                    themeClasses.bgPrimary,
                    themeClasses.textPrimary,
                    themeClasses.borderPrimary,
                    'border'
                  )}
                />
                <p className={cx('text-sm mt-1', themeClasses.textMuted)}>
                  Target reject ratio percentage (0-100). Leave empty to remove target line.
                </p>
              </div>

              {/* Max Downtime Target */}
              <div>
                <label className={cx('block text-sm font-medium mb-2', themeClasses.textPrimary)}>
                  {t('maxDowntimeTarget') || 'Max Downtime Target'} (hours)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  value={maxDowntimeTarget}
                  onChange={(e) => setMaxDowntimeTarget(e.target.value)}
                  placeholder="e.g., 2.0"
                  className={cx(
                    'w-full p-3 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                    themeClasses.bgPrimary,
                    themeClasses.textPrimary,
                    themeClasses.borderPrimary,
                    'border'
                  )}
                />
                <p className={cx('text-sm mt-1', themeClasses.textMuted)}>
                  Maximum allowable downtime in hours per day/week. Leave empty to remove target line.
                </p>
              </div>

              {/* Min MTBF Target */}
              <div>
                <label className={cx('block text-sm font-medium mb-2', themeClasses.textPrimary)}>
                  {t('minMTBFTarget') || 'Min MTBF Target'} (minutes)
                </label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={minMTBFTarget}
                  onChange={(e) => setMinMTBFTarget(e.target.value)}
                  placeholder="e.g., 120"
                  className={cx(
                    'w-full p-3 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                    themeClasses.bgPrimary,
                    themeClasses.textPrimary,
                    themeClasses.borderPrimary,
                    'border'
                  )}
                />
                <p className={cx('text-sm mt-1', themeClasses.textMuted)}>
                  Minimum time between failures in minutes. Leave empty to remove target line.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-3">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className={cx(
                    "flex-1 py-3 px-4 rounded-md font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed",
                    themeClasses.bgInfo,
                    themeClasses.textWhite,
                    "hover:opacity-90"
                  )}
                >
                  {saving ? 'Saving...' : 'Save Targets'}
                </button>
                <button
                  onClick={handleDelete}
                  disabled={saving}
                  className={cx(
                    "px-4 py-3 rounded-md font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed",
                    themeClasses.bgError,
                    themeClasses.textWhite,
                    "hover:opacity-90"
                  )}
                >
                  Delete
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Right Panel - Current Targets Overview */}
        <div className="space-y-6">
          <h3 className={cx('text-lg font-semibold', themeClasses.textPrimary)}>Current Targets</h3>
          
          {Object.keys(targetConfiguration.targets).length === 0 ? (
            <div className="text-center py-8">
              <div className="text-4xl mb-2">🎯</div>
              <p className={cx('font-medium', themeClasses.textPrimary)}>No targets configured yet</p>
              <p className={cx('text-sm', themeClasses.textMuted)}>Select a machine type to configure targets</p>
            </div>
          ) : (
            <div className="space-y-4">
              {Object.values(targetConfiguration.targets).map((target) => (
                <div
                  key={target.machineType}
                  className={cx(
                    'p-4 border rounded-md cursor-pointer transition-colors',
                    selectedMachineType === target.machineType
                      ? 'border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20'
                      : cx(themeClasses.borderPrimary, themeClasses.hover)
                  )}
                  onClick={() => setSelectedMachineType(target.machineType)}
                >
                  <div className={cx('font-medium mb-2', themeClasses.textPrimary)}>{target.machineType}</div>
                  
                  <div className="space-y-1 text-sm">
                    {target.targets.efficiency !== undefined && (
                      <div className="flex justify-between">
                        <span className={themeClasses.textSecondary}>Efficiency:</span>
                        <span className={cx('font-medium', themeClasses.textPrimary)}>{target.targets.efficiency}%</span>
                      </div>
                    )}
                    {target.targets.utilization !== undefined && (
                      <div className="flex justify-between">
                        <span className={themeClasses.textSecondary}>Utilization:</span>
                        <span className={cx('font-medium', themeClasses.textPrimary)}>{target.targets.utilization}%</span>
                      </div>
                    )}
                    {target.targets.rejectRatio !== undefined && (
                      <div className="flex justify-between">
                        <span className={themeClasses.textSecondary}>Reject Ratio:</span>
                        <span className={cx('font-medium', themeClasses.textPrimary)}>{target.targets.rejectRatio}%</span>
                      </div>
                    )}
                    {target.targets.maxDowntime !== undefined && (
                      <div className="flex justify-between">
                        <span className={themeClasses.textSecondary}>Max Downtime:</span>
                        <span className={cx('font-medium', themeClasses.textPrimary)}>{target.targets.maxDowntime}h</span>
                      </div>
                    )}
                    {target.targets.minMTBF !== undefined && (
                      <div className="flex justify-between">
                        <span className={themeClasses.textSecondary}>Min MTBF:</span>
                        <span className={cx('font-medium', themeClasses.textPrimary)}>{target.targets.minMTBF}m</span>
                      </div>
                    )}
                  </div>
                  
                  <div className={cx('text-xs mt-2', themeClasses.textMuted)}>
                    Updated: {new Date(target.lastUpdated).toLocaleDateString()}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Sync Information */}
          <div className={cx('p-4 rounded-md', themeClasses.bgSecondary)}>
            <h4 className={cx('font-medium mb-2', themeClasses.textPrimary)}>Sync Status</h4>
            <div className={cx('text-sm space-y-1', themeClasses.textSecondary)}>
              <div>Version: {targetConfiguration.version}</div>
              <div>Last synced: {new Date(targetConfiguration.lastSyncedAt).toLocaleString()}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TargetManagement;
